import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { remarkApi, F10C } from '../../../services/remarkApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useRemarkForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F10C[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [desc, setDesc] = useState('');
  const [content, setContent] = useState('');
  const [isOld, setIsOld] = useState(false);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await remarkApi.list(1, 1000); setRecords(res.data || []); return res.data || []; }
    catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.f10c_01); setDesc(current.f10c_11);
      setContent(current.f10c_12); setIsOld(current.f10c_13);
    }
  }, [current, mode]);

  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => { setMode('add'); setCode(''); setDesc(''); setContent(''); setIsOld(false); };
  const startEdit = () => {
    if (!current) return; setMode('edit');
    setCode(current.f10c_01); setDesc(current.f10c_11); setContent(current.f10c_12); setIsOld(current.f10c_13);
  };
  const cancel = () => {
    setMode('browse');
    if (current) { setCode(current.f10c_01); setDesc(current.f10c_11); setContent(current.f10c_12); setIsOld(current.f10c_13); }
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        await remarkApi.create({ f10c_01: code, f10c_11: desc, f10c_12: content, f10c_13: isOld });
        message.success('新增成功');
      } else {
        await remarkApi.update(current!.f10c_01, { f10c_11: desc, f10c_12: content, f10c_13: isOld });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const t = mode === 'add' ? code : current!.f10c_01;
      const idx = data.findIndex(r => r.f10c_01 === t);
      setCurrentIdx(idx >= 0 ? idx : 0); setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await remarkApi.delete(current.f10c_01); message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1)); else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f10c_01 >= t);
    if (idx >= 0) setCurrentIdx(idx); else message.warning('查無此代號');
  };

  return { mode, isEditing, loading, records, current, currentIdx,
    code, setCode, desc, setDesc, content, setContent, isOld, setIsOld,
    navFirst, navPrev, navNext, navLast, startAdd, startEdit, cancel, save, doDelete, quickSearch };
}
